import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  getRentabilidadTransporte,
} from "../helpers/rutaReportes";

import {
  getTransportes,
} from "../helpers/rutaTransportes";

const dinero = (valor) =>
  Number(valor || 0).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
  });

const fechaTexto = (valor) => {
  if (!valor) return "-";
  const [anio, mes, dia] = valor.split("-");
  return `${dia}/${mes}/${anio}`;
};

const fechaLocal = (primero = false) => {
  const fecha = new Date();

  if (primero) fecha.setDate(1);

  const anio = fecha.getFullYear();
  const mes = String(fecha.getMonth() + 1)
    .padStart(2, "0");
  const dia = String(fecha.getDate())
    .padStart(2, "0");

  return `${anio}-${mes}-${dia}`;
};

const porcentaje = (valor) =>
  valor == null
    ? "-"
    : `${Number(valor).toLocaleString("es-AR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`;

const camposCostos = [
  ["combustible", "Combustible"],
  ["viaticos", "Viáticos"],
  ["gastos", "Gastos"],
  ["costoChofer", "Costo chofer"],
  ["costoTotal", "Costo total"],
];

export default function InformeViajesTransporte() {
  const [transportes, setTransportes] = useState([]);
  const [transporteId, setTransporteId] = useState("");
  const [buscar, setBuscar] = useState("");
  const [desde, setDesde] = useState(
    () => fechaLocal(true)
  );
  const [hasta, setHasta] = useState(
    () => fechaLocal()
  );

  const [informe, setInforme] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const ocupado = useRef(false);
  const activo = useRef(true);

  useEffect(() => {
    activo.current = true;

    let cancelar = false;

    async function cargarTransportes() {
      try {
        const respuesta = await getTransportes();

        if (respuesta?.ok === false) {
          throw new Error(
            respuesta.msg ||
            "No se pudieron cargar los transportes."
          );
        }

        const raw =
          respuesta?.transportes ||
          respuesta?.data?.transportes ||
          respuesta;

        const opciones = (
          Array.isArray(raw) ? raw : []
        )
          .map((item) => ({
            id: String(item._id || item.id || ""),
            nombre:
              item.transporte ||
              item.razonsocial ||
              item.razonSocial ||
              item.nombre ||
              "SIN NOMBRE",
          }))
          .filter((item) => item.id)
          .sort((a, b) =>
            a.nombre.localeCompare(b.nombre, "es")
          );

        if (!cancelar) setTransportes(opciones);
      } catch (e) {
        if (!cancelar) {
          setError(
            e.message ||
            "No se pudieron cargar los transportes."
          );
        }
      }
    }

    cargarTransportes();

    return () => {
      cancelar = true;
      activo.current = false;
    };
  }, []);

  async function consultar() {
    if (ocupado.current) return;

    setError("");

    if (!transporteId || !desde || !hasta) {
      setError("Seleccione transporte y fechas.");
      return;
    }

    if (desde > hasta) {
      setError("Desde no puede ser posterior a Hasta.");
      return;
    }

    ocupado.current = true;
    setLoading(true);
    setInforme(null);

    try {
      const respuesta =
        await getRentabilidadTransporte({
          transporteId,
          desde,
          hasta,
        });

      if (!respuesta?.ok) {
        throw new Error(
          respuesta?.error ||
          "No se pudo obtener el informe."
        );
      }

      if (activo.current) {
        setInforme(respuesta);
      }
    } catch (e) {
      if (activo.current) {
        setError(e.message || "Error al consultar.");
      }
    } finally {
      ocupado.current = false;

      if (activo.current) setLoading(false);
    }
  }

  const opciones = transportes.filter(
    (item) =>
      item.id === transporteId ||
      item.nombre
        .toLowerCase()
        .includes(buscar.trim().toLowerCase())
  );

  const resumen = informe?.resumen;
  const detalle = informe?.detalle || [];

  const tarjetas = resumen
    ? [
        [
          "Viajes / rendiciones",
          `${resumen.cantidadComandas} / ${resumen.cantidadRendiciones}`,
          "",
        ],
        [
          "Ingresos con rendición",
          dinero(resumen.ingresoEvaluado),
          "text-primary",
        ],
        [
          "Costos registrados",
          dinero(resumen.costoTotal),
          "",
        ],
        [
          "Resultado",
          dinero(resumen.resultado),
          resumen.resultado >= 0
            ? "text-success"
            : "text-danger",
        ],
        [
          "Margen sobre ingreso",
          porcentaje(resumen.margen),
          "",
        ],
      ]
    : [];

  return (
    <Container fluid className="py-3">
      <div className="mb-3">
        <h4 className="mb-1">
          Rentabilidad de viajes por transporte
        </h4>

        <small className="text-muted">
          Ingresos con IVA y costos registrados en
          las rendiciones. Período por fecha de comanda principal.
        </small>
      </div>

      <Card className="shadow-sm border-0">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Label>Transporte</Form.Label>
              <Form.Select
                value={transporteId}
                disabled={loading}
                onChange={(e) =>
                  setTransporteId(e.target.value)
                }
              >
                <option value="">
                  — Seleccionar —
                </option>

                {opciones.map((item) => (
                  <option
                    key={item.id}
                    value={item.id}
                  >
                    {item.nombre}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={2}>
              <Form.Label>Buscar transporte</Form.Label>
              <Form.Control
                value={buscar}
                disabled={loading}
                placeholder="Filtrar..."
                onChange={(e) =>
                  setBuscar(e.target.value)
                }
              />
            </Col>

            <Col md={2}>
              <Form.Label>Desde</Form.Label>
              <Form.Control
                type="date"
                value={desde}
                disabled={loading}
                onChange={(e) =>
                  setDesde(e.target.value)
                }
              />
            </Col>

            <Col md={2}>
              <Form.Label>Hasta</Form.Label>
              <Form.Control
                type="date"
                value={hasta}
                disabled={loading}
                onChange={(e) =>
                  setHasta(e.target.value)
                }
              />
            </Col>

            <Col md={2}>
              <Button
                className="w-100"
                disabled={loading}
                onClick={consultar}
              >
                {loading
                  ? "Consultando..."
                  : "Consultar"}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      )}

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <span className="ms-2">
            Consultando rendiciones...
          </span>
        </div>
      )}

      {informe && (
        <>
          <div className="mt-3 text-muted">
            <strong>{informe.transporte.nombre}</strong>
            <span>
              {" · "}
              {fechaTexto(informe.filtros.desde)}
              {" al "}
              {fechaTexto(informe.filtros.hasta)}
            </span>
          </div>

          <Row className="g-2 mt-1">
            {tarjetas.map(([titulo, valor, clase]) => (
              <Col
                key={titulo}
                xs={12}
                sm={6}
                lg
              >
                <Card className="h-100 shadow-sm border-0">
                  <Card.Body>
                    <small className="text-muted">
                      {titulo}
                    </small>

                    <div
                      className={`fs-5 fw-bold ${clase}`}
                    >
                      {valor}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {(resumen.sinRendicion > 0 ||
            resumen.revisarKm > 0 ||
            resumen.sinIngreso > 0) && (
            <Alert variant="warning" className="mt-3">
              <div>
                Sin rendición: {resumen.sinRendicion}.
                Revisar kilómetros: {resumen.revisarKm}.
                Ingreso cero o negativo: {resumen.sinIngreso}.
              </div>

              <div>
                El resultado total incluye solamente
                comandas con rendición y kilómetros válidos.
              </div>
            </Alert>
          )}

          <Card className="mt-3 shadow-sm border-0">
            <Card.Body>
              <div className="table-responsive">
                <Table
                  striped
                  bordered
                  hover
                  size="sm"
                  className="mb-0"
                  style={{
                    fontSize: 12,
                    whiteSpace: "nowrap",
                  }}
                >
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Comanda</th>
                      <th>Viaje</th>
                      <th>Chofer</th>
                      <th>Factura</th>
                      <th>Rendiciones</th>
                      <th className="text-end">Ingreso c/IVA</th>

                      {camposCostos.map(([campo, nombre]) => (
                        <th
                          key={campo}
                          className="text-end"
                        >
                          {nombre}
                        </th>
                      ))}

                      <th className="text-end">Adelantos</th>
                      <th className="text-end">Resultado</th>
                      <th className="text-end">Margen</th>
                      <th>Estado</th>
                    </tr>
                  </thead>

                  <tbody>
                    {detalle.map((fila) => (
                      <tr key={fila.comandaId}>
                        <td>{fechaTexto(fila.fechaYMD)}</td>
                        <td>{fila.codcom}</td>
                        <td>{fila.nroviaje}</td>
                        <td>{fila.chofer || "-"}</td>
                        <td>{fila.factura || "-"}</td>
                        <td>
                          {fila.rendiciones
                            .map((v) => v.nroViaje)
                            .join(", ") || "-"}
                        </td>

                        <td className="text-end">
                          {dinero(fila.ingreso)}
                        </td>

                        {camposCostos.map(([campo]) => (
                          <td
                            key={campo}
                            className="text-end"
                          >
                            {fila.evaluable
                              ? dinero(fila[campo])
                              : "-"}
                          </td>
                        ))}

                        <td className="text-end">
                          {fila.cantidadRendiciones > 0
                            ? dinero(fila.adelantos)
                            : "-"}
                        </td>

                        <td
                          className={`text-end fw-bold ${
                            !fila.evaluable
                              ? "text-muted"
                              : fila.resultado >= 0
                              ? "text-success"
                              : "text-danger"
                          }`}
                        >
                          {fila.evaluable
                            ? dinero(fila.resultado)
                            : "-"}
                        </td>

                        <td className="text-end">
                          {porcentaje(fila.margen)}
                        </td>

                        <td>{fila.estado}</td>
                      </tr>
                    ))}

                    {detalle.length === 0 && (
                      <tr>
                        <td
                          colSpan={16}
                          className="text-center py-4"
                        >
                          No hay comandas para este período.
                        </td>
                      </tr>
                    )}
                  </tbody>

                  <tfoot>
                    <tr className="fw-bold">
                      <td colSpan={6}>
                        TOTAL CON RENDICIÓN VÁLIDA
                      </td>

                      <td className="text-end">
                        {dinero(resumen.ingresoEvaluado)}
                      </td>

                      {camposCostos.map(([campo]) => (
                        <td
                          key={campo}
                          className="text-end"
                        >
                          {dinero(resumen[campo])}
                        </td>
                      ))}

                      <td className="text-end">
                        {dinero(resumen.adelantos)}
                      </td>

                      <td
                        className={`text-end ${
                          resumen.resultado >= 0
                            ? "text-success"
                            : "text-danger"
                        }`}
                      >
                        {dinero(resumen.resultado)}
                      </td>

                      <td className="text-end">
                        {porcentaje(resumen.margen)}
                      </td>

                      <td />
                    </tr>
                  </tfoot>
                </Table>
              </div>

              <small className="d-block text-muted mt-3">
                Adelantos informativos: no se suman al costo.
                Ingreso de todas las comandas del período:
                {" "}
                {dinero(resumen.ingresoTotal)}.
                El resultado refleja los costos cargados
                hasta el momento de la consulta.
              </small>
            </Card.Body>
          </Card>
        </>
      )}
    </Container>
  );
}